// src/pages/VehicleV2/HireVehicleDetailsEntry.jsx

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { FaSave, FaArrowLeft, FaTimes, FaSyncAlt } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/hireDetailsEntry";

const sVal = (v) => (v === null || v === undefined ? "" : String(v));

const toInputDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const readQS = () => {
  const sp = new URLSearchParams(window.location.search);

  const getAny = (...keys) => {
    for (const k of keys) {
      const v = sp.get(k);
      if (v !== null && v !== undefined && String(v).trim() !== "") return v;
    }
    return "";
  };

  return {
    vehicleId: getAny("vehicleId", "vehicleid", "VehicleId", "VehicleID"),
    vehicledetid: getAny("vehicledetid", "vehicleDetId", "vehicledetId", "VehicleDetId"),
    vehicleCode: getAny("vehicleCode", "vehiclecode", "VehicleCode"),
  };
};

const emptyForm = {
  storeId: "",
  vehicleSubTypeId: "",
  vehicleBasisId: "",
  fuelTypeId: "",
  tdsPercentId: "",
  vehicleCode: "",
  vehicleName: "",
  vehicleRegistrationNumber: "",
  model: "",
  hiredFrom: "",
  engineSUNumber: "",
  vehicleSINumber: "",
  address1: "",
  address2: "",
  city: "",
  telephone: "",
  adminApprovalDate: "",
  fcExpire: "",
  icExpire: "",
  foodForDriver: "",
  chequeFavourName: "",
  remarks: "",
  vehicleRent: "",
  ownersName: "",
  contactPerson: "",
  mobileNo: "",
  validFrom: "",
  validUpto: "",
  permitExpire: "",
  panCardNumber: "",
  panHolderName: "",
  mailingAddress: "",
  hasFillingHr: "no",
  hasFillingKm: "no",
};

const HireVehicleDetailsEntry = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const qs = useMemo(() => readQS(), []);
  const editVehicleId = Number(qs.vehicleId || 0);
  const editVehDetId = Number(qs.vehicledetid || 0);
  const isEdit = editVehicleId > 0;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const loginUserId =
    localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const branchId = Number(
    localStorage.getItem("branchId") || localStorage.getItem("branchid") || 8
  );
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  const [loading, setLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [ddlLoaded, setDdlLoaded] = useState(false);

  const [ddl, setDdl] = useState({
    stores: [],
    vehicleSubTypes: [],
    vehicleBasis: [],
    fuelTypes: [],
    tdsPercents: [],
    foodOptions: [
      { id: "Yes", name: "Yes" },
      { id: "No", name: "No" },
    ],
  });

  const [formData, setFormData] = useState(emptyForm);

  const setField = (name, value) => setFormData((p) => ({ ...p, [name]: value }));

  const onChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  const loadNextCode = async () => {
    if (isEdit) return;
    try {
      setCodeLoading(true);
      const res = await axios.get(`${API_BASE}/nextCode`, {
        params: { branchId },
        headers: commonHeaders,
      });
      const code = res.data?.data?.vehicleCode;
      if (code) {
        setField("vehicleCode", code);
      } else {
        toast.error("Vehicle code not generated");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Could not generate vehicle code");
    } finally {
      setCodeLoading(false);
    }
  };

  /* ===================== 1) DROPDOWNS + AUTO CODE ===================== */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setDdlLoaded(false);

        const res = await axios.get(`${API_BASE}/dropdowns`, {
          params: { branchId },
          headers: commonHeaders,
        });

        const data = res.data?.data || {};
        setDdl((p) => ({
          ...p,
          stores: data.stores || [],
          vehicleSubTypes: data.vehicleSubTypes || [],
          vehicleBasis: data.vehicleBasis || [],
          fuelTypes: data.fuelTypes || [],
          tdsPercents: data.tdsPercents || [],
          foodOptions: data.foodOptions || p.foodOptions,
        }));

        if (!isEdit) {
          try {
            const codeRes = await axios.get(`${API_BASE}/nextCode`, {
              params: { branchId },
              headers: commonHeaders,
            });
            const code = codeRes.data?.data?.vehicleCode;
            if (code) {
              setFormData((p) => ({ ...p, vehicleCode: code }));
            }
          } catch (codeErr) {
            console.error(codeErr);
            toast.error("Could not generate vehicle code");
          }
        }

        setDdlLoaded(true);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load dropdowns");
      } finally {
        setLoading(false);
      }
    })();
  }, [branchId, commonHeaders, isEdit]);

  /* ===================== 2) EDIT LOAD ===================== */
  useEffect(() => {
    if (!editVehicleId) return;
    if (!ddlLoaded) return;

    (async () => {
      try {
        setLoading(true);

        const res = await axios.get(`${API_BASE}/view`, {
          params: { vehicleId: editVehicleId, vehicledetid: editVehDetId },
          headers: commonHeaders,
        });

        const master = res.data?.data?.master || {};
        const det = res.data?.data?.details || {};

        const storeId = sVal(master.fld_storeid ?? master.storeId ?? "");
        const subTypeId = sVal(
          master.fld_vehicle_subtypeid ?? master.vehicleSubTypeId ?? ""
        );
        const basisId = sVal(
          master.fld_vehicle_basis ?? master.fld_vehicle_basisid ?? ""
        );
        const fuelTypeId = sVal(
          det.fld_fueltype ?? det.fld_fueltypeid ?? det.fuelTypeId ?? ""
        );

        setFormData((p) => ({
          ...p,
          storeId,
          vehicleSubTypeId: subTypeId,
          vehicleBasisId: basisId,
          fuelTypeId,
          tdsPercentId: sVal(det.fld_TDSpercentid ?? det.fld_tdspercentid ?? ""),
          vehicleCode: sVal(master.fld_vehicle_code ?? qs.vehicleCode ?? ""),
          vehicleName: sVal(master.fld_vehicle_name ?? ""),
          vehicleRegistrationNumber: sVal(master.fld_vehicle_regno ?? ""),
          model: sVal(master.fld_vehicle_model ?? ""),
          hiredFrom: sVal(det.fld_hiredfrom ?? ""),
          engineSUNumber: sVal(det.fld_engineSLNO ?? ""),
          vehicleSINumber: sVal(det.fld_equipmentSLNO ?? ""),
          address1: sVal(det.fld_address1 ?? ""),
          address2: sVal(det.fld_address2 ?? ""),
          city: sVal(det.fld_city ?? ""),
          telephone: sVal(det.fld_telephone ?? ""),
          adminApprovalDate: toInputDate(det.fld_admin_approvaldate),
          fcExpire: toInputDate(det.fld_FCExpire),
          icExpire: toInputDate(det.fld_ICExpire),
          foodForDriver: sVal(det.fld_foodfordriver ?? ""),
          chequeFavourName: sVal(det.fld_chq_favouringname ?? ""),
          remarks: sVal(det.fld_remarks ?? ""),
          vehicleRent: sVal(det.fld_vehicle_rent ?? ""),
          ownersName: sVal(det.fld_ownersname ?? ""),
          contactPerson: sVal(det.fld_contactperson ?? ""),
          mobileNo: sVal(det.fld_mobileno ?? ""),
          validFrom: toInputDate(det.fld_validfrom),
          validUpto: toInputDate(det.fld_validupto),
          permitExpire: toInputDate(det.fld_permitdetails),
          panCardNumber: sVal(det.fld_pan_cardno ?? ""),
          panHolderName: sVal(det.fld_pan_holdername ?? ""),
          mailingAddress: sVal(det.fld_mailingaddress ?? ""),
          hasFillingHr: sVal(master.fld_hsdfillinghr ?? "0") === "1" ? "yes" : "no",
          hasFillingKm: sVal(master.fld_hsdfillingkm ?? "0") === "1" ? "yes" : "no",
        }));

        if (subTypeId) {
          try {
            const tdsRes = await axios.get(`${API_BASE}/tds`, {
              params: { vehicleSubTypeId: Number(subTypeId) },
              headers: commonHeaders,
            });
            setDdl((p) => ({ ...p, tdsPercents: tdsRes.data?.data || [] }));
          } catch {
            // ignore
          }
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load edit data");
      } finally {
        setLoading(false);
      }
    })();
  }, [editVehicleId, editVehDetId, ddlLoaded, qs.vehicleCode, commonHeaders]);

  /* ===================== 3) TDS ON SUBTYPE ===================== */
  useEffect(() => {
    if (!formData.vehicleSubTypeId) return;

    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/tds`, {
          params: { vehicleSubTypeId: Number(formData.vehicleSubTypeId) },
          headers: commonHeaders,
        });
        setDdl((p) => ({ ...p, tdsPercents: res.data?.data || [] }));
      } catch {
        // ignore
      }
    })();
  }, [formData.vehicleSubTypeId, commonHeaders]);

  /* ===================== 4) AUTO HSD FLAGS FROM BASIS ===================== */
  useEffect(() => {
    if (!formData.vehicleBasisId) return;

    const basis = ddl.vehicleBasis.find(
      (x) => sVal(x.id) === sVal(formData.vehicleBasisId)
    );
    const basisName = sVal(basis?.name).toLowerCase();

    if (basisName === "km") {
      setFormData((p) => ({ ...p, hasFillingKm: "yes", hasFillingHr: "no" }));
    } else if (basisName === "hour") {
      setFormData((p) => ({ ...p, hasFillingKm: "no", hasFillingHr: "yes" }));
    }
  }, [formData.vehicleBasisId, ddl.vehicleBasis]);

  const handleClear = () => {
    setFormData(emptyForm);
    toast.info("Form cleared!");
    if (!isEdit) {
      setTimeout(() => loadNextCode(), 0);
    }
  };

  const validate = () => {
    const required = [
      ["storeId", "Store"],
      ["vehicleCode", "Vehicle Code"],
      ["vehicleName", "Vehicle Name"],
      ["hiredFrom", "Hired From"],
      ["vehicleSubTypeId", "Vehicle Sub Type"],
      ["vehicleBasisId", "Vehicle Basis"],
      ["fuelTypeId", "Fuel Type"],
    ];
    const missing = required.filter(([k]) => !sVal(formData[k]).trim());
    if (missing.length) {
      toast.error(
        `Please fill required fields: ${missing.map((x) => x[1]).join(", ")}`
      );
      return false;
    }
    if (!branchId || !loginUserId) {
      toast.error("branchId / loginUserId missing in localStorage");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      branchId,
      loginUserId,
      vehicleId: editVehicleId || 0,
      vehicledetid: editVehDetId || 0,
      store: Number(formData.storeId),
      vehicleSubType: Number(formData.vehicleSubTypeId),
      vehicleBasis: Number(formData.vehicleBasisId),
      fuelType: Number(formData.fuelTypeId),
      tds: formData.tdsPercentId ? Number(formData.tdsPercentId) : "",
      vehicleCode: formData.vehicleCode,
      vehicleName: formData.vehicleName,
      vehicleRegistrationNumber: formData.vehicleRegistrationNumber,
      model: formData.model,
      hiredFrom: formData.hiredFrom,
      engineSUNumber: formData.engineSUNumber,
      vehicleSINumber: formData.vehicleSINumber,
      address1: formData.address1,
      address2: formData.address2,
      city: formData.city,
      telephone: formData.telephone,
      adminApprovalDate: formData.adminApprovalDate,
      fcExpire: formData.fcExpire,
      icExpire: formData.icExpire,
      foodForDriver: formData.foodForDriver,
      chequeFavourName: formData.chequeFavourName,
      remarks: formData.remarks,
      vehicleRent: formData.vehicleRent,
      ownersName: formData.ownersName,
      contactPerson: formData.contactPerson,
      mobileNo: formData.mobileNo,
      validFrom: formData.validFrom,
      validUpto: formData.validUpto,
      permitExpire: formData.permitExpire,
      panCardNumber: formData.panCardNumber,
      panHolderName: formData.panHolderName,
      mailingAddress: formData.mailingAddress,
      hasFillingHr: formData.hasFillingHr,
      hasFilling: formData.hasFillingKm,
    };

    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });
      toast.success(
        res.data?.message || (isEdit ? "Updated successfully!" : "Saved successfully!")
      );
      if (!isEdit) {
        setTimeout(() => navigate(-1), 800);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">HIRE VEHICLE DETAILS ENTRY</span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Hire Vehicle Details {isEdit ? "Edit" : "Entry"}
              </h2>
              {isEdit ? (
                <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Vehicle Code: {qs.vehicleCode || formData.vehicleCode || "-"}
                </p>
              ) : (
                <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Vehicle code is generated automatically
                </p>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate(-1)}
                disabled={loading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft />
                Back
              </button>

              <button
                onClick={handleSave}
                disabled={loading}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                  loading ? "opacity-60 cursor-not-allowed" : ""
                } ${buttonClass} ${hoverClass} text-white`}
              >
                <FaSave />
                {loading ? "Saving..." : isEdit ? "Update Details" : "Save Details"}
              </button>
            </div>
          </div>

          {/* BASIC INFORMATION */}
          <Section title="Basic Information" isDark={isDark}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <FieldSelect
                label="Store *"
                name="storeId"
                value={formData.storeId}
                onChange={onChange}
                options={[{ id: "", name: "Select Store" }, ...ddl.stores]}
                inputClass={inputClass}
                labelClass={labelClass}
              />

              <div>
                <label className={labelClass}>Vehicle Code *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="vehicleCode"
                    value={formData.vehicleCode}
                    onChange={onChange}
                    readOnly={!isEdit}
                    className={`${inputClass} ${
                      !isEdit ? "opacity-90 cursor-not-allowed" : ""
                    }`}
                  />
                  {!isEdit && (
                    <button
                      type="button"
                      onClick={loadNextCode}
                      disabled={codeLoading}
                      className={`px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap flex items-center gap-2 ${
                        isDark
                          ? "bg-gray-700 border border-gray-600 text-white hover:bg-gray-600"
                          : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                      title="Generate next code"
                    >
                      <FaSyncAlt className={codeLoading ? "animate-spin" : ""} />
                      {codeLoading ? "..." : "Generate"}
                    </button>
                  )}
                </div>
              </div>

              <FieldInput
                label="Vehicle Name *"
                name="vehicleName"
                value={formData.vehicleName}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldSelect
                label="Vehicle Sub Type *"
                name="vehicleSubTypeId"
                value={formData.vehicleSubTypeId}
                onChange={onChange}
                options={[
                  { id: "", name: "Select Vehicle Sub Type" },
                  ...ddl.vehicleSubTypes,
                ]}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                label="Vehicle Registration Number"
                name="vehicleRegistrationNumber"
                value={formData.vehicleRegistrationNumber}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                label="Model"
                name="model"
                value={formData.model}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
            </div>
          </Section>

          {/* CONTACT INFO */}
          <Section title="Contact Info" isDark={isDark}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <FieldInput
                label="Hired From *"
                name="hiredFrom"
                value={formData.hiredFrom}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                label="Engine SL No"
                name="engineSUNumber"
                value={formData.engineSUNumber}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                label="City"
                name="city"
                value={formData.city}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                label="Address 1"
                name="address1"
                value={formData.address1}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                label="Address 2"
                name="address2"
                value={formData.address2}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                label="Telephone"
                name="telephone"
                value={formData.telephone}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                type="date"
                label="Admin Approval Date"
                name="adminApprovalDate"
                value={formData.adminApprovalDate}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                type="date"
                label="FC Expire"
                name="fcExpire"
                value={formData.fcExpire}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                type="date"
                label="IC Expire"
                name="icExpire"
                value={formData.icExpire}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldSelect
                label="Food For Driver"
                name="foodForDriver"
                value={formData.foodForDriver}
                onChange={onChange}
                options={[{ id: "", name: "Select" }, ...ddl.foodOptions]}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldSelect
                label="TDS %"
                name="tdsPercentId"
                value={formData.tdsPercentId}
                onChange={onChange}
                options={[{ id: "", name: "Select" }, ...ddl.tdsPercents]}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                label="Cheque Favour Name"
                name="chequeFavourName"
                value={formData.chequeFavourName}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <div className="md:col-span-2 lg:col-span-3">
                <FieldTextarea
                  label="Remarks"
                  name="remarks"
                  value={formData.remarks}
                  onChange={onChange}
                  inputClass={inputClass}
                  labelClass={labelClass}
                />
              </div>
              <div>
                <label className={labelClass}>Had Filling Hr</label>
                <RadioYesNo
                  name="hasFillingHr"
                  value={formData.hasFillingHr}
                  onChange={onChange}
                  isDark={isDark}
                />
                <p className={`mt-1 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Yes = diesel tracked by hour meter
                </p>
              </div>
            </div>
          </Section>

          {/* VEHICLE BASIS */}
          <Section title="Vehicle Basis" isDark={isDark}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <FieldSelect
                label="Vehicle Basis *"
                name="vehicleBasisId"
                value={formData.vehicleBasisId}
                onChange={onChange}
                options={[
                  { id: "", name: "Select Vehicle Basis" },
                  ...ddl.vehicleBasis,
                ]}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                label="Equipment SI No"
                name="vehicleSINumber"
                value={formData.vehicleSINumber}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                type="number"
                label="Vehicle Rent"
                name="vehicleRent"
                value={formData.vehicleRent}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                label="Owners Name"
                name="ownersName"
                value={formData.ownersName}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                label="Contact Person"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                label="Mobile No"
                name="mobileNo"
                value={formData.mobileNo}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                type="date"
                label="Valid From"
                name="validFrom"
                value={formData.validFrom}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                type="date"
                label="Valid Upto"
                name="validUpto"
                value={formData.validUpto}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                type="date"
                label="Permit Expire"
                name="permitExpire"
                value={formData.permitExpire}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldSelect
                label="Fuel Type *"
                name="fuelTypeId"
                value={formData.fuelTypeId}
                onChange={onChange}
                options={[{ id: "", name: "Select Fuel Type" }, ...ddl.fuelTypes]}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                label="PAN Card No"
                name="panCardNumber"
                value={formData.panCardNumber}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <FieldInput
                label="PAN Holder Name"
                name="panHolderName"
                value={formData.panHolderName}
                onChange={onChange}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <div className="md:col-span-2 lg:col-span-3">
                <FieldTextarea
                  label="Mailing Address"
                  name="mailingAddress"
                  value={formData.mailingAddress}
                  onChange={onChange}
                  inputClass={inputClass}
                  labelClass={labelClass}
                />
              </div>
              <div>
                <label className={labelClass}>Had Filling KM</label>
                <RadioYesNo
                  name="hasFillingKm"
                  value={formData.hasFillingKm}
                  onChange={onChange}
                  isDark={isDark}
                />
                <p className={`mt-1 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Yes = diesel tracked by odometer (KM)
                </p>
              </div>
            </div>
          </Section>

          <div className="flex justify-between items-center mt-2 pt-6 border-t border-gray-200 dark:border-gray-700 flex-wrap gap-3">
            <button
              onClick={handleClear}
              disabled={loading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                isDark
                  ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                  : "border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              <FaTimes />
              Clear
            </button>

            <div className="flex gap-3">
              <button
                onClick={() => navigate(-1)}
                disabled={loading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                  isDark
                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                }`}
              >
                <FaArrowLeft />
                Back
              </button>

              <button
                onClick={handleSave}
                disabled={loading}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  loading ? "opacity-60 cursor-not-allowed" : ""
                } ${buttonClass} ${hoverClass} text-white`}
              >
                <FaSave />
                {isEdit ? "Update" : "Save"}
              </button>
            </div>
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default HireVehicleDetailsEntry;

function Section({ title, isDark, children }) {
  return (
    <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function FieldInput({
  label,
  name,
  value,
  onChange,
  type = "text",
  inputClass,
  labelClass,
}) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className={inputClass}
      />
    </div>
  );
}

function FieldTextarea({
  label,
  name,
  value,
  onChange,
  rows = 3,
  inputClass,
  labelClass,
}) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={rows}
        className={inputClass}
      />
    </div>
  );
}

function FieldSelect({
  label,
  name,
  value,
  onChange,
  options,
  idKey = "id",
  nameKey = "name",
  inputClass,
  labelClass,
}) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <select name={name} value={value} onChange={onChange} className={inputClass}>
        {options.map((o, idx) => (
          <option key={idx} value={o[idKey]}>
            {o[nameKey]}
          </option>
        ))}
      </select>
    </div>
  );
}

function RadioYesNo({ name, value, onChange, isDark }) {
  return (
    <div className="flex gap-6 pt-1">
      {["yes", "no"].map((opt) => (
        <label key={opt} className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name={name}
            value={opt}
            checked={value === opt}
            onChange={onChange}
            className="w-4 h-4 text-blue-600 focus:ring-blue-500"
          />
          <span className={isDark ? "text-gray-300" : "text-gray-700"}>
            {opt.charAt(0).toUpperCase() + opt.slice(1)}
          </span>
        </label>
      ))}
    </div>
  );
}